import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../store';
import axios from 'axios';
import * as fromAuth from './../../auth/state';
import get from 'lodash.get';
import { getAccessToken } from './../../auth/state';
import { LabRequest } from '../../../interfaces/lab-request';
import { AuditTrail } from '../../../interfaces/audit-trail';

const BASE_API = process.env['REACT_APP_HUM_ADMIN_API_URL'] ?? ' ';
const instance = axios.create({
  baseURL: BASE_API,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export const camelCase = (input: string) =>
  input.toLowerCase().replace(/-(.)/g, (match, group1) => group1.toUpperCase());

interface Paging {
  page: number;
  pageSize: number;
  startKey?: string;
  sortType?: 'asc' | 'desc';
  sortField?: string;
  search?: string;
}

interface AuditTrailState {
  list: LabRequest[];
  paginationKeys: string[];
  isNextPage: boolean;
  isPrevPage: boolean;
  paging: Paging;
  loading: boolean;
  listError: string | null;
  current: LabRequest | null;
  currentError: string | null;
  createEventOpen: boolean;
  createEventData: { code: string; labRequestId: string } | null;
  newEvent: { timestamp: string; metadata: any } | null;
  createEventError: string | null;
  creatingNewEvent: boolean;
  filters: { roaPassed: boolean | ''; genotypingPass: boolean | '' };
}

const initialState: AuditTrailState = {
  list: [],
  paginationKeys: [],
  isNextPage: false,
  isPrevPage: false,
  paging: {
    page: 0,
    pageSize: 30,
    sortType: 'desc',
    sortField: 'fulfillmentMailSentTimestamp',
    startKey: '',
  },
  loading: false,
  listError: null,
  current: null,
  currentError: null,
  createEventOpen: false,
  createEventData: null,
  newEvent: null,
  createEventError: null,
  creatingNewEvent: false,
  filters: { roaPassed: '', genotypingPass: '' },
};

export const labRequestsSlice = createSlice({
  name: 'auditTrail',
  initialState,
  reducers: {
    startLoading: (state, action: PayloadAction<Paging | undefined>) => {
      if (action.payload) {
        const { page: currentPage } = state.paging;
        const { page: newPage } = action.payload;
        const isNextPage = newPage > currentPage;
        const isPrevPage = newPage < currentPage;
        state.paging = action.payload;
        state.isNextPage = isNextPage;
        state.isPrevPage = isPrevPage;
        if (newPage === 0) {
          state.paginationKeys = [];
        }
        if (isPrevPage) {
          state.paginationKeys.pop();
        }
      }
      state.loading = true;
    },
    loadLabRequestsSuccess: (state, action: PayloadAction<LabRequest[]>) => {
      state.loading = false;
      state.list = action.payload;
      const lastItem = action.payload[action.payload.length - 1];
      const nextPageAvailable = action.payload.length === state.paging.pageSize;
      if (
        lastItem &&
        (state.isNextPage || state.paging.page === 0 || (!state.isNextPage && !state.isPrevPage)) &&
        nextPageAvailable
      ) {
        state.paginationKeys.push(lastItem.id);
      }
      state.listError = '';
    },
    loadLabRequestsError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.listError = action.payload;
    },
    clearCurrent: state => {
      state.current = null;
      state.currentError = '';
      const isLastPage = state.list.length < state.paging.pageSize;
      if (!isLastPage) {
        state.paginationKeys.pop();
      }
    },
    loadLabRequestByIdSuccess: (state, action: PayloadAction<LabRequest>) => {
      state.loading = false;
      state.current = action.payload;
      state.currentError = '';
    },
    loadLabRequestByIdError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.currentError = action.payload;
    },
    openCreateAuditTrail: (
      state,
      action: PayloadAction<{ code: string; labRequestId: string }>
    ) => {
      state.createEventOpen = true;
      state.createEventData = action.payload;
    },
    closeCreateAuditTrail: state => {
      state.createEventOpen = false;
      state.createEventData = null;
      state.creatingNewEvent = false;
      state.createEventError = '';
      state.newEvent = null;
    },
    startCreatingNewEvent: (state, action: PayloadAction<{ timestamp: string; metadata: any }>) => {
      state.creatingNewEvent = true;
      state.createEventError = '';
      state.newEvent = action.payload;
    },
    createAuditTrailSuccess: (state, action: PayloadAction<AuditTrail>) => {
      state.createEventOpen = false;
      state.creatingNewEvent = false;
      state.createEventError = '';
      state.newEvent = null;
    },
    createAuditTrailError: (state, action: PayloadAction<string>) => {
      state.creatingNewEvent = false;
      state.createEventError = action.payload;
    },
    filter: (
      state,
      action: PayloadAction<{ roaPassed: boolean | ''; genotypingPass: boolean | '' }>
    ) => {
      const { roaPassed, genotypingPass } = action.payload;
      state.filters.genotypingPass = genotypingPass;
      state.filters.roaPassed = roaPassed;
    },
  },
  extraReducers: {
    [fromAuth.logoutSuccess.type]: state => (state = initialState),
  },
});

export const {
  startLoading,
  loadLabRequestsSuccess,
  loadLabRequestsError,
  clearCurrent,
  loadLabRequestByIdSuccess,
  loadLabRequestByIdError,
  openCreateAuditTrail,
  closeCreateAuditTrail,
  startCreatingNewEvent,
  createAuditTrailSuccess,
  createAuditTrailError,
  filter,
} = labRequestsSlice.actions;

export const loadLabRequests =
  (paging: Paging): AppThunk =>
  async (dispatch, getState) => {
    const currentPaging = getState().auditTrail.paging;
    const pageSizeChanged = paging.pageSize !== currentPaging.pageSize;
    const page = pageSizeChanged ? 0 : paging.page;
    dispatch(startLoading({ ...paging, page }));
    const paginationKeys = getState().auditTrail.paginationKeys;
    const startKey =
      paginationKeys.length && page > 0 ? paginationKeys[paginationKeys.length - 1] : '';
    const { roaPassed, genotypingPass } = getState().auditTrail.filters;
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.get<LabRequest[]>(`lab-requests`, {
          params: {
            ...paging,
            startKey,
            ...(roaPassed !== '' && { 'countyResultPostedMetadata.roaPassed': roaPassed }),
            ...(genotypingPass !== '' && {
              'dnaGenotypingResultPostedMetadata.genotypingPass': genotypingPass,
            }),
          },
          headers: {
            Authorization: `Bearer ${accessToken.getJwtToken()}`,
          },
        });
        dispatch(loadLabRequestsSuccess(response.data));
        return response.data;
      } else {
        dispatch(loadLabRequestsError('Missing token'));
      }
    } catch (e: any) {
      dispatch(loadLabRequestsError(get(e, 'response.data.error', e.message)));
    }
  };

export const loadLabRequestById =
  (id: string): AppThunk =>
  async dispatch => {
    dispatch(startLoading());
    dispatch(clearCurrent());
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.get<LabRequest>(`lab-requests/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken.getJwtToken()}`,
          },
        });
        dispatch(loadLabRequestByIdSuccess(response.data));
      } else {
        dispatch(loadLabRequestByIdError('Missing token'));
      }
    } catch (e: any) {
      dispatch(loadLabRequestByIdError(get(e, 'response.data.error', e.message)));
    }
  };

export const createAuditTrail =
  (data: { metadata: any; timestamp: string }): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startCreatingNewEvent(data));
    try {
      const state = getState();
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.post<AuditTrail>(
          `audit-trails`,
          {
            ...data,
            ...state.auditTrail.createEventData,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken.getJwtToken()}`,
            },
          }
        );
        dispatch(createAuditTrailSuccess(response.data));
        return response.data;
      } else {
        dispatch(createAuditTrailError('Missing token'));
      }
    } catch (e: any) {
      dispatch(createAuditTrailError(get(e, 'response.data.error', e.message)));
    }
  };

export default labRequestsSlice.reducer;
